import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { getDeviceToken } from '../pages/customer/feed/LikeShareBar';
import { Publication } from './usePublications';

export interface PublicationAnalytics {
  publication_id: string;
  views_count: number;
  clicks_count: number;
  shares_count: number;
  unique_customers: number;
  average_duration_seconds: number;
  engagement_rate: number;
}

export interface UsePublicationDetailReturn {
  publication: Publication | null;
  analytics: PublicationAnalytics | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  loadAnalytics: () => Promise<void>;
}

export const usePublicationDetail = (
  publicationId: string,
  tenantId?: string
): UsePublicationDetailReturn => {
  const [publication, setPublication] = useState<Publication | null>(null);
  const [analytics, setAnalytics] = useState<PublicationAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPublication = useCallback(async () => {
    if (!publicationId) return;

    try {
      setLoading(true);
      setError(null);

      const publicationData = await api.get(`/publications/${publicationId}`, {
        params: { device_token: getDeviceToken() },
        headers: tenantId ? { 'X-Tenant-ID': tenantId } : {},
      });

      setPublication(publicationData);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement de la publication');
      console.error('Error fetching publication:', err);
    } finally {
      setLoading(false);
    }
  }, [publicationId, tenantId]);

  const loadAnalytics = useCallback(async () => {
    if (!publicationId || !tenantId) return;

    try {
      const analyticsData = await api.get(`/publications/${publicationId}/analytics`, {
        headers: { 'X-Tenant-ID': tenantId },
      });

      setAnalytics(analyticsData);
    } catch (err: any) {
      console.error('Error fetching analytics:', err);
      // Ne pas bloquer si les analytics échouent
    }
  }, [publicationId, tenantId]);

  const refresh = useCallback(async () => {
    await fetchPublication();
    await loadAnalytics();
  }, [fetchPublication, loadAnalytics]);

  useEffect(() => {
    fetchPublication();
  }, [fetchPublication]);

  return {
    publication,
    analytics,
    loading,
    error,
    refresh,
    loadAnalytics,
  };
};
